// Planification du distributeur (E1, E2) : besoins reçus des stations et établissements (décision motivée),
// commandes, propositions d'affectation calculées par le serveur et jamais imposées, émission d'un ordre de
// transport avec réservation sur la position du propriétaire au dépôt ; refus si le disponible est insuffisant,
// sauf dérogation accordée par le dépôt (D-026) ; programmation des chargements par jour et par dépôt.

import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { LIBELLES_ETATS_COMMANDE, deProduit, type EtatCommande } from '@carburanttrack/shared/logistique';
import { api, ErreurApi, messageErreur } from '../../api/client';
import { Indisponible, Message, MessageErreur, Squelette } from '../../composants/Etats';
import { date, dateHeure, libelleProvince } from '../../composants/format';
import { localVersIso, maintenantLocal } from '../stocks/composants';
import { libelleEtatBesoin, libelleUrgence, quantite } from './communs';
import { nomProduit } from './Station';
import type { Besoin, Proposition, ReferentielEmission } from './types';

interface VuePlanification {
  maintenant: string;
  urgences: Besoin[];
  aDecider: Besoin[];
  aPlanifier: Besoin[];
  programmation: { jour: string; depots: { depot: string; ordres: { id: string; numero: string; volume: string; produit: string; heure: string }[] }[] }[];
}

interface Commande {
  id: string;
  reference: string;
  etat: string;
  commentaire: string | null;
  creeLe: string;
  besoins: { id: string; reference: string; etat: string; quantite: string; produitCode: string }[];
}

const tonUrgence = (u: string) => (u === 'prioritaire' ? 'rouge' : u === 'urgente' ? 'ambre' : '');

function LigneBesoin({ b, children }: { b: Besoin; children?: ReactNode }) {
  return (
    <li>
      <span>
        <b>
          {b.reference}, {quantite(b.quantite, b.unite)} {deProduit(b.produitCode)} {b.urgence !== 'normale' && <span className={`etiquette ${tonUrgence(b.urgence)}`}>{libelleUrgence(b.urgence)}</span>}{' '}
          <span className="etiquette">{libelleEtatBesoin(b.etat)}</span>
        </b>
        <small>
          {b.installationNom ?? 'installation indisponible'}, {libelleProvince(b.provinceCode)} ; souhaité le {date(b.dateSouhaitee)}
          {Number(b.quantiteLivree) > 0 ? ` ; déjà livré ${quantite(b.quantiteLivree, b.unite)}` : ''}
          {b.source === 'hors_ligne' ? ' ; saisi hors connexion' : ''}
        </small>
        {b.commentaire && <small>{b.commentaire}</small>}
        {b.motif && <small>Motif : {b.motif}</small>}
      </span>
      {children && <span className="actions">{children}</span>}
    </li>
  );
}

// --- Commandes : besoins reçus et regroupements ---------------------------------------------------------------

export function PageCommandes() {
  const client = useQueryClient();
  const besoins = useQuery({ queryKey: ['besoins'], queryFn: async () => (await api<Besoin[]>('/api/v1/besoins?limite=300')).donnees });
  const commandes = useQuery({ queryKey: ['commandes'], queryFn: async () => (await api<Commande[]>('/api/v1/commandes')).donnees });
  const [decision, setDecision] = useState<{ id: string; action: 'acceptation' | 'refus' } | null>(null);
  const [motif, setMotif] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const [commentaire, setCommentaire] = useState('');
  const [nbCommandes, setNbCommandes] = useState(15);
  const [nbBesoins, setNbBesoins] = useState(20);
  const decider = useMutation({
    mutationFn: async () => api(`/api/v1/besoins/${decision!.id}/${decision!.action}`, { corps: decision!.action === 'refus' ? { motif } : { motif: motif.trim() || null } }),
    onSuccess: () => {
      setDecision(null);
      setMotif('');
      void client.invalidateQueries({ queryKey: ['besoins'] });
      void client.invalidateQueries({ queryKey: ['planification'] });
    },
  });
  const regrouper = useMutation({
    mutationFn: async () => api<Commande>('/api/v1/commandes', { corps: { besoins: selection, commentaire: commentaire.trim() || null } }),
    onSuccess: () => {
      setSelection([]);
      setCommentaire('');
      void client.invalidateQueries({ queryKey: ['besoins'] });
      void client.invalidateQueries({ queryKey: ['commandes'] });
    },
  });
  const aDecider = besoins.data?.filter((b) => b.etat === 'soumis') ?? [];
  const acceptesLibres = besoins.data?.filter((b) => ['accepte'].includes(b.etat) && !b.commandeId) ?? [];
  const autres = besoins.data?.filter((b) => !['soumis'].includes(b.etat) && !(b.etat === 'accepte' && !b.commandeId)) ?? [];
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Commandes</h1>
          <p className="attenue">Besoins exprimés par vos stations et les établissements, à accepter ou refuser avec un motif, puis à regrouper en commandes et à planifier.</p>
        </div>
        <Link className="bouton primaire" to="/planification">
          Planifier les livraisons
        </Link>
      </div>
      {besoins.isLoading && <Squelette lignes={5} />}
      {besoins.error && <MessageErreur erreur={besoins.error} titre="Besoins indisponibles" />}
      {besoins.data && (
        <>
          <section className="carte-bloc" aria-labelledby="titre-a-decider">
            <div className="carte-bloc-tete">
              <h2 id="titre-a-decider">À décider</h2>
              <span className="attenue">{aDecider.length} besoin{aDecider.length > 1 ? 's' : ''}</span>
            </div>
            {aDecider.length === 0 ? (
              <p className="attenue">Aucun besoin en attente de décision.</p>
            ) : (
              <ul className="liste-lignes">
                {aDecider.map((b) => (
                  <LigneBesoin key={b.id} b={b}>
                    <button type="button" className="bouton" onClick={() => setDecision({ id: b.id, action: 'acceptation' })}>
                      Accepter
                    </button>
                    <button type="button" className="bouton" onClick={() => setDecision({ id: b.id, action: 'refus' })}>
                      Refuser
                    </button>
                  </LigneBesoin>
                ))}
              </ul>
            )}
            {decision && (
              <form
                className="formulaire-ligne"
                onSubmit={(e) => {
                  e.preventDefault();
                  decider.mutate();
                }}
              >
                <div className="champ">
                  <label htmlFor="b-motif">{decision.action === 'refus' ? 'Motif du refus (communiqué au demandeur)' : 'Commentaire (facultatif)'}</label>
                  <input id="b-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required={decision.action === 'refus'} minLength={decision.action === 'refus' ? 3 : undefined} maxLength={500} />
                </div>
                <div className="actions">
                  <button type="submit" className="bouton primaire" disabled={decider.isPending || (decision.action === 'refus' && motif.trim().length < 3)}>
                    {decision.action === 'refus' ? 'Refuser le besoin' : 'Accepter le besoin'}
                  </button>
                  <button type="button" className="bouton" onClick={() => setDecision(null)}>
                    Annuler
                  </button>
                </div>
                {decider.error && <Message type="erreur">{messageErreur(decider.error)}</Message>}
              </form>
            )}
          </section>
          <section className="carte-bloc" aria-labelledby="titre-regrouper">
            <div className="carte-bloc-tete">
              <h2 id="titre-regrouper">Besoins acceptés hors commande</h2>
              <span className="attenue">Sélectionnez des besoins pour les regrouper en une commande</span>
            </div>
            {acceptesLibres.length === 0 ? (
              <p className="attenue">Aucun besoin accepté en attente de regroupement.</p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  regrouper.mutate();
                }}
              >
                {acceptesLibres.map((b) => (
                  <label className="case" key={b.id}>
                    <input type="checkbox" checked={selection.includes(b.id)} onChange={(e) => setSelection(e.target.checked ? [...selection, b.id] : selection.filter((x) => x !== b.id))} />
                    <span>
                      {b.reference}, {quantite(b.quantite, b.unite)} {deProduit(b.produitCode)}
                      <small>
                        {b.installationNom ?? 'installation indisponible'}, souhaité le {date(b.dateSouhaitee)}
                      </small>
                    </span>
                  </label>
                ))}
                <div className="champ">
                  <label htmlFor="c-commentaire">Commentaire de la commande</label>
                  <input id="c-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={1000} />
                </div>
                <div className="actions">
                  <button type="submit" className="bouton primaire" disabled={selection.length === 0 || regrouper.isPending}>
                    Créer la commande ({selection.length})
                  </button>
                </div>
                {regrouper.error && <Message type="erreur">{messageErreur(regrouper.error)}</Message>}
              </form>
            )}
          </section>
        </>
      )}
      <section className="carte-bloc" aria-labelledby="titre-commandes">
        <div className="carte-bloc-tete">
          <h2 id="titre-commandes">Commandes</h2>
          {commandes.data && commandes.data.length > 0 && (
            <span className="attenue">
              {formaterNombre(Math.min(nbCommandes, commandes.data.length))} affichées sur {formaterNombre(commandes.data.length)}, les plus récentes d’abord
            </span>
          )}
        </div>
        {commandes.isLoading && <Squelette lignes={3} />}
        {commandes.error && <MessageErreur erreur={commandes.error} />}
        {commandes.data && commandes.data.length === 0 && <p className="attenue">Aucune commande.</p>}
        {commandes.data && commandes.data.length > 0 && (
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Commandes du distributeur</caption>
              <thead>
                <tr>
                  <th scope="col">Commande</th>
                  <th scope="col">État</th>
                  <th scope="col">Besoins</th>
                  <th scope="col" className="masquable">
                    Créée le
                  </th>
                </tr>
              </thead>
              <tbody>
                {commandes.data.slice(0, nbCommandes).map((c) => (
                  <tr key={c.id}>
                    <td>
                      {c.reference}
                      {c.commentaire && <small>{c.commentaire}</small>}
                    </td>
                    <td>
                      <span className="etiquette">{LIBELLES_ETATS_COMMANDE[c.etat as EtatCommande] ?? c.etat}</span>
                    </td>
                    <td>
                      {c.besoins.map((b) => `${b.reference} (${quantite(b.quantite, 'L')} ${nomProduit(b.produitCode).toLowerCase()}, ${libelleEtatBesoin(b.etat).toLowerCase()})`).join(' ; ') || <span className="attenue">aucun</span>}
                    </td>
                    <td className="masquable">{date(c.creeLe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {commandes.data && commandes.data.length > nbCommandes && (
          <div className="actions">
            <button type="button" className="bouton" onClick={() => setNbCommandes(nbCommandes + 25)}>
              Afficher {formaterNombre(Math.min(25, commandes.data.length - nbCommandes))} commandes de plus
            </button>
          </div>
        )}
      </section>
      {besoins.data && autres.length > 0 && (
        <section className="carte-bloc" aria-labelledby="titre-historique-besoins">
          <div className="carte-bloc-tete">
            <h2 id="titre-historique-besoins">Historique des besoins</h2>
            <span className="attenue">
              {formaterNombre(Math.min(nbBesoins, autres.length))} affichés sur {formaterNombre(autres.length)}
            </span>
          </div>
          <ul className="liste-lignes">
            {autres.slice(0, nbBesoins).map((b) => (
              <LigneBesoin key={b.id} b={b} />
            ))}
          </ul>
          {autres.length > nbBesoins && (
            <div className="actions">
              <button type="button" className="bouton" onClick={() => setNbBesoins(nbBesoins + 25)}>
                Afficher {formaterNombre(Math.min(25, autres.length - nbBesoins))} besoins de plus
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// --- Planification et émission ---------------------------------------------------------------------------------

export function PagePlanification() {
  const [params] = useSearchParams();
  const vue = useQuery({ queryKey: ['planification'], queryFn: async () => (await api<VuePlanification>('/api/v1/planification')).donnees });
  const [besoinChoisi, setBesoinChoisi] = useState<Besoin | null>(null);
  const [proposition, setProposition] = useState<Proposition | null>(null);
  const [libre, setLibre] = useState(params.get('nouvel') === '1');
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Planification</h1>
          <p className="attenue">Les propositions d’affectation sont calculées à partir de votre disponible par dépôt et des véhicules libres de vos transporteurs ; elles ne sont jamais imposées.</p>
        </div>
        <button
          type="button"
          className="bouton"
          onClick={() => {
            setBesoinChoisi(null);
            setProposition(null);
            setLibre(true);
          }}
        >
          Émettre un ordre sans besoin
        </button>
      </div>
      {vue.isLoading && <Squelette lignes={6} />}
      {vue.error && <MessageErreur erreur={vue.error} titre="Planification indisponible" />}
      {vue.data && (
        <div className="deux-colonnes">
          <div className="principal">
            {vue.data.urgences.length > 0 && (
              <section className="carte-bloc" aria-labelledby="titre-urgences">
                <h2 id="titre-urgences">Urgences</h2>
                <ol className="liste-exceptions">
                  {vue.data.urgences.map((b, i) => (
                    <li key={b.id}>
                      <span className={`exception ${b.urgence === 'prioritaire' ? 'rouge' : 'ambre'}`}>
                        <span className="numero" aria-hidden="true">
                          {i + 1}
                        </span>
                        <span className="texte">
                          <span className="marque-exception">
                            {libelleUrgence(b.urgence)}, {libelleEtatBesoin(b.etat).toLowerCase()}
                          </span>
                          <b>
                            {b.installationNom ?? b.reference}, {quantite(b.quantite, b.unite)} {deProduit(b.produitCode)}
                          </b>
                          <small>Souhaité le {date(b.dateSouhaitee)}</small>
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}
            {vue.data.aDecider.length > 0 && (
              <Message type="info">
                {vue.data.aDecider.length} besoin{vue.data.aDecider.length > 1 ? 's' : ''} à accepter ou refuser avant planification. <Link to="/commandes">Voir les commandes</Link>
              </Message>
            )}
            <section className="carte-bloc" aria-labelledby="titre-a-planifier">
              <div className="carte-bloc-tete">
                <h2 id="titre-a-planifier">À planifier</h2>
                <span className="attenue">Besoins acceptés ou partiellement livrés</span>
              </div>
              {vue.data.aPlanifier.length === 0 ? (
                <p className="attenue">Aucun besoin accepté en attente de planification.</p>
              ) : (
                <ul className="liste-lignes">
                  {vue.data.aPlanifier.map((b) => (
                    <LigneBesoin key={b.id} b={b}>
                      <button
                        type="button"
                        className="bouton"
                        aria-pressed={besoinChoisi?.id === b.id}
                        onClick={() => {
                          setBesoinChoisi(b);
                          setProposition(null);
                          setLibre(false);
                        }}
                      >
                        Propositions
                      </button>
                    </LigneBesoin>
                  ))}
                </ul>
              )}
            </section>
            {besoinChoisi && <Propositions besoin={besoinChoisi} retenue={proposition} retenir={setProposition} />}
            {(besoinChoisi || libre) && <FormulaireEmission key={`${besoinChoisi?.id ?? 'libre'}-${proposition?.id ?? ''}`} besoin={besoinChoisi} proposition={proposition} fermer={() => (setBesoinChoisi(null), setProposition(null), setLibre(false))} />}
          </div>
          <aside className="lateral">
            <section className="carte-bloc" aria-labelledby="titre-programmation">
              <h2 id="titre-programmation">Chargements programmés</h2>
              {vue.data.programmation.length === 0 ? (
                <p className="attenue">Aucun ordre émis en attente de chargement.</p>
              ) : (
                vue.data.programmation.map((j) => (
                  <div key={j.jour}>
                    <h3>{date(j.jour)}</h3>
                    {j.depots.map((d) => (
                      <div key={d.depot}>
                        <b>{d.depot}</b>
                        <ul className="liste-lignes">
                          {d.ordres.map((o) => (
                            <li key={o.id}>
                              <Link to={`/livraisons/${o.id}`}>
                                <span>
                                  <b>{o.numero}</b>
                                  <small>
                                    {quantite(o.volume, 'L')} {deProduit(o.produit)}, {dateHeure(o.heure).slice(-5)}
                                  </small>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

function Propositions({ besoin, retenue, retenir }: { besoin: Besoin; retenue: Proposition | null; retenir: (p: Proposition | null) => void }) {
  const client = useQueryClient();
  const calcul = useMutation({ mutationFn: async () => (await api<Proposition[]>(`/api/v1/besoins/${besoin.id}/propositions`, { corps: {} })).donnees });
  const existantes = useQuery({ queryKey: ['besoin', besoin.id], queryFn: async () => (await api<Besoin & { propositions: Proposition[] }>(`/api/v1/besoins/${besoin.id}`)).donnees });
  const liste = calcul.data ?? existantes.data?.propositions ?? null;
  return (
    <section className="carte-bloc" aria-labelledby="titre-propositions">
      <div className="carte-bloc-tete">
        <h2 id="titre-propositions">Propositions pour {besoin.reference}</h2>
        <button type="button" className="bouton" disabled={calcul.isPending} onClick={() => calcul.mutate(undefined, { onSuccess: () => void client.invalidateQueries({ queryKey: ['besoin', besoin.id] }) })}>
          {calcul.isPending ? 'Calcul en cours' : 'Calculer les propositions'}
        </button>
      </div>
      {calcul.error && <Message type="erreur">{messageErreur(calcul.error)}</Message>}
      {existantes.isLoading && <Squelette lignes={2} />}
      {liste && liste.length === 0 && <p className="attenue">Aucune proposition : aucun dépôt ne détient de disponible suffisant pour vous, ou aucun véhicule n’est libre. Vous pouvez émettre l’ordre manuellement.</p>}
      {liste && liste.length > 0 && (
        <div className="grille-cartes">
          {liste.map((p) => (
            <button type="button" key={p.id} className="carte-selection" aria-pressed={retenue?.id === p.id} onClick={() => retenir(retenue?.id === p.id ? null : p)} disabled={Boolean(p.ordreTransportId)}>
              <span className="carte-selection-tete">
                <span>
                  <b>
                    {p.rang}. {p.depot.nom}
                  </b>
                  <small>Disponible {quantite(p.disponible, 'L')}{p.couvreBesoin ? ', couvre le besoin' : ', couverture partielle'}</small>
                </span>
              </span>
              <small>
                {p.transporteur?.nom ?? 'transporteur à choisir'}
                {p.vehicule ? `, ${p.vehicule.immatriculation ?? 'véhicule'} (${quantite(p.vehicule.capacite, 'L')})` : ', aucun véhicule libre'}
                {p.distanceKm ? `, ${p.distanceKm} km` : ''}
              </small>
              <small>
                Chargement {dateHeure(p.dateChargementProposee)}, arrivée estimée {dateHeure(p.dateArriveeEstimee)}
              </small>
              {p.motifs.length > 0 && <small>{p.motifs.join(' ; ')}</small>}
              {p.ordreTransportId && <small>Retenue : ordre émis</small>}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function FormulaireEmission({ besoin, proposition, fermer }: { besoin: Besoin | null; proposition: Proposition | null; fermer: () => void }) {
  const client = useQueryClient();
  const ref = useQuery({ queryKey: ['referentiel-emission'], queryFn: async () => (await api<ReferentielEmission>('/api/v1/planification/referentiel')).donnees });
  const [depot, setDepot] = useState(proposition?.depot.id ?? '');
  const [destination, setDestination] = useState(besoin?.installationId ?? '');
  const [produit, setProduit] = useState(besoin?.produitCode ?? 'gasoil');
  const [volume, setVolume] = useState(besoin ? String(Math.max(0, Number(besoin.quantite) - Number(besoin.quantiteLivree))) : '');
  const [transporteur, setTransporteur] = useState(proposition?.transporteur?.id ?? '');
  const [vehicule, setVehicule] = useState(proposition?.vehicule?.id ?? '');
  const [conducteur, setConducteur] = useState('');
  const [chargement, setChargement] = useState(proposition ? new Date(Date.parse(proposition.dateChargementProposee) + 3_600_000).toISOString().slice(0, 16) : maintenantLocal());
  const [arriveeLe, setArriveeLe] = useState(proposition ? new Date(Date.parse(proposition.dateArriveeEstimee) + 3_600_000).toISOString().slice(0, 16) : maintenantLocal());
  const [derogation, setDerogation] = useState('');
  const [manque, setManque] = useState<string | null>(null);
  const emission = useMutation({
    mutationFn: async () =>
      (
        await api<{ id: string; numero: string }>('/api/v1/ordres-transport', {
          corps: {
            besoinId: besoin?.id ?? null,
            propositionId: proposition?.id ?? null,
            installationChargementId: depot,
            installationDestinationId: destination,
            produitCode: produit,
            volumePrevu: volume.replace(/\s/g, '').replace(',', '.'),
            transporteurId: transporteur,
            vehiculeId: vehicule || null,
            conducteurId: conducteur || null,
            dateChargementPrevue: localVersIso(chargement),
            dateArriveePrevue: localVersIso(arriveeLe),
            derogationId: derogation.trim() || null,
          },
        })
      ).donnees,
    onError: (e) => setManque(e instanceof ErreurApi && e.statut === 409 && (e.details as { code?: string } | null)?.code === 'disponible_insuffisant' ? e.message : null),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['planification'] });
      void client.invalidateQueries({ queryKey: ['besoins'] });
      void client.invalidateQueries({ queryKey: ['suivi-missions'] });
    },
  });
  if (ref.isLoading) return <Squelette lignes={4} />;
  if (ref.error || !ref.data) return <MessageErreur erreur={ref.error} titre="Référentiel d’émission indisponible" />;
  const r = ref.data;
  const t = r.transporteurs.find((x) => x.id === transporteur);
  const d = r.depots.find((x) => x.id === depot);
  const dispo = d?.produits.find((p) => p.produitCode === produit)?.disponible ?? null;
  const destinations = r.destinations.filter((x) => x.produits.includes(produit));
  if (emission.data)
    return (
      <Message type="succes">
        Ordre <Link to={`/livraisons/${emission.data.id}`}>{emission.data.numero}</Link> émis : le volume est réservé sur votre position au dépôt et le canal de service de la livraison est ouvert aux parties.{' '}
        <button type="button" className="bouton discret" onClick={fermer}>
          Fermer
        </button>
      </Message>
    );
  return (
    <form
      className="carte-bloc formulaire-ligne"
      aria-labelledby="titre-emission"
      onSubmit={(e) => {
        e.preventDefault();
        setManque(null);
        emission.mutate();
      }}
    >
      <h2 id="titre-emission" className="pleine-ligne">
        Émettre un ordre de transport{besoin ? ` pour ${besoin.reference}` : ''}
      </h2>
      {r.depots.length === 0 && <Indisponible motif="aucune position de stock à votre nom dans un dépôt" />}
      <div className="champ">
        <label htmlFor="e-produit">Produit</label>
        <select id="e-produit" value={produit} onChange={(e) => setProduit(e.target.value)} disabled={Boolean(besoin)}>
          {[...new Set(r.depots.flatMap((x) => x.produits.map((p) => p.produitCode)))].map((p) => (
            <option key={p} value={p}>
              {nomProduit(p)}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="e-depot">Dépôt de chargement</label>
        <select id="e-depot" value={depot} onChange={(e) => setDepot(e.target.value)} required>
          <option value="">Choisir</option>
          {r.depots
            .filter((x) => x.produits.some((p) => p.produitCode === produit))
            .map((x) => {
              const v = x.produits.find((p) => p.produitCode === produit)?.disponible;
              return (
                <option key={x.id} value={x.id}>
                  {x.nom}, disponible {v === null || v === undefined ? 'indisponible' : quantite(v, 'L')}
                </option>
              );
            })}
        </select>
        {d && <span className="aide">Votre disponible : {dispo === null ? 'indisponible (position non calculée)' : quantite(dispo, 'L')}</span>}
      </div>
      <div className="champ">
        <label htmlFor="e-destination">Destination</label>
        <select id="e-destination" value={destination} onChange={(e) => setDestination(e.target.value)} required disabled={Boolean(besoin)}>
          <option value="">Choisir</option>
          {(besoin && !destinations.some((x) => x.id === besoin.installationId) ? [{ id: besoin.installationId, nom: besoin.installationNom ?? 'installation du besoin', provinceCode: besoin.provinceCode }] : []).concat(destinations).map((x) => (
            <option key={x.id} value={x.id}>
              {x.nom}, {libelleProvince(x.provinceCode)}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="e-volume">Volume prévu (litres)</label>
        <input id="e-volume" inputMode="decimal" value={volume} onChange={(e) => setVolume(e.target.value)} required />
      </div>
      <div className="champ">
        <label htmlFor="e-transporteur">Transporteur sous contrat</label>
        <select
          id="e-transporteur"
          value={transporteur}
          onChange={(e) => {
            setTransporteur(e.target.value);
            setVehicule('');
            setConducteur('');
          }}
          required
        >
          <option value="">Choisir</option>
          {r.transporteurs.map((x) => (
            <option key={x.id} value={x.id}>
              {x.nom}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="e-vehicule">Véhicule</label>
        <select id="e-vehicule" value={vehicule} onChange={(e) => setVehicule(e.target.value)}>
          <option value="">Laissé au transporteur</option>
          {t?.vehicules.map((v) => (
            <option key={v.id} value={v.id} disabled={v.engage}>
              {v.immatriculation}, {quantite(v.capacite, 'L')}, {v.compartiments} compartiment{v.compartiments > 1 ? 's' : ''}
              {v.engage ? ' (engagé)' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="e-conducteur">Conducteur</label>
        <select id="e-conducteur" value={conducteur} onChange={(e) => setConducteur(e.target.value)}>
          <option value="">Laissé au transporteur</option>
          {t?.conducteurs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nom}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="e-chargement">Chargement prévu</label>
        <input id="e-chargement" type="datetime-local" value={chargement} onChange={(e) => setChargement(e.target.value)} required />
      </div>
      <div className="champ">
        <label htmlFor="e-arrivee">Arrivée prévue</label>
        <input id="e-arrivee" type="datetime-local" value={arriveeLe} onChange={(e) => setArriveeLe(e.target.value)} required />
      </div>
      {(manque || derogation) && (
        <div className="champ pleine-ligne">
          <label htmlFor="e-derogation">Référence de la dérogation accordée par le dépôt (D-026)</label>
          <input id="e-derogation" value={derogation} onChange={(e) => setDerogation(e.target.value)} pattern="[0-9a-fA-F-]{36}" />
          <span className="aide">La dérogation est accordée par une personne habilitée du dépôt, depuis son écran des chargements ; elle est tracée et signalée.</span>
        </div>
      )}
      {manque && <Message type="avertissement">{manque}</Message>}
      {emission.error && !manque && <Message type="erreur">{messageErreur(emission.error)}</Message>}
      <div className="actions pleine-ligne">
        <button type="submit" className="bouton primaire" disabled={emission.isPending || !depot || !destination || !transporteur || !volume}>
          Émettre l’ordre
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

